import { useCallback, useState } from 'react'
import { shareEqual } from '@/ui/share'
import type { Candidate } from '@/views/user/teamPick'

// 사람을 고르는 창이 고른 사람들 — 처음 값은 부르는 쪽이 준 사람들이다(사용자 목록에서 체크한 줄, 팀의 지금 멤버).
// 고르는 자리의 목록은 서버가 찾은 한 번의 읽기(`views/user/PeoplePicker.tsx`)라, 고른 사람을 그 목록에서 다시 찾으면
// 찾는 말을 바꾸거나 목록이 다시 읽힐 때 목록 밖의 사람이 조용히 빠지고 적용이 그 사람을 건너뛴다.
// 그래서 고른 것은 id 가 아니라 사람째로 쥐고, 고른 목록·이름·적용의 대상은 이것만 읽는다.
// 쥔 줄은 고른 순간의 것이라, 찾기가 다시 읽히면 그 읽기에 있는 사람은 새 줄로 바꿔 쥔다(`renew`) — 창을 연 사이
// 바뀐 권한·직위·이름이 고른 목록에 옛 값으로 남지 않게. 찾기 밖의 사람은 바뀌지 않으므로 쥔 값으로 적용을 정하지 않는다.
// 한 번 고른 사람은 풀어도 이름으로 남는다(`known`) — 빼거나 내릴 사람을 이름으로 보여야 한다.

export type Picked = {
  /** 고른 사람, 고른 순서. */
  chosen: Candidate[]
  ids: string[]
  /** 이 창이 아는 사람 — 처음 준 사람과 한 번이라도 고른 사람. */
  known: (id: string) => Candidate | undefined
  /** 고르기와 풀기. 뒤집기가 아니라 값 지정이다 — 줄과 체크박스 두 경로가 겹쳐 불려도 결과가 같다. */
  set: (p: Candidate, on: boolean) => void
  /** 이 사람들만 남긴다 — 적용이 끝나면 실패한 사람만 남는다. */
  only: (ids: Set<string>) => void
  /** 새로 읽은 줄 — 이 창이 아는 사람 가운데 그 줄에 있는 사람을 바꿔 쥔다. 같은 줄이면 쥔 객체 그대로다. */
  renew: (rows: Candidate[]) => void
}

/**
 * `keeps` 는 풀 수 없는 사람이다. 지금 고른 id 들을 받는다 — 끌기가 여럿을 한 번에 지나가도 그 순간의 고르기로 판정한다.
 */
export function usePicked(seed: Candidate[], keeps?: (id: string, ids: string[]) => boolean): Picked {
  const [state, setState] = useState(() => ({
    people: new Map(seed.map((c) => [c.user.id, c] as const)),
    ids: seed.map((c) => c.user.id),
  }))
  const set = (p: Candidate, on: boolean) =>
    setState((s) => {
      const id = p.user.id
      const has = s.ids.includes(id)
      if (on) {
        // 다시 고른 사람은 새로 읽은 줄로 바꿔 쥔다.
        if (has && s.people.get(id) === p) return s
        return { people: new Map(s.people).set(id, p), ids: has ? s.ids : [...s.ids, id] }
      }
      if (!has || keeps?.(id, s.ids)) return s
      return { people: s.people, ids: s.ids.filter((x) => x !== id) }
    })
  const only = (keep: Set<string>) => setState((s) => ({ people: s.people, ids: s.ids.filter((id) => keep.has(id)) }))
  // 고르는 자리가 읽기마다 부르므로 같은 함수로 둔다.
  const renew = useCallback(
    (rows: Candidate[]) =>
      setState((s) => {
        let people: Map<string, Candidate> | null = null
        for (const row of rows) {
          const held = s.people.get(row.user.id)
          if (held === undefined) continue
          const next = shareEqual(held, row)
          if (next === held) continue
          people ??= new Map(s.people)
          people.set(row.user.id, next)
        }
        return people === null ? s : { people, ids: s.ids }
      }),
    [],
  )
  return {
    chosen: state.ids.flatMap((id) => state.people.get(id) ?? []),
    ids: state.ids,
    known: (id) => state.people.get(id),
    set,
    only,
    renew,
  }
}
